import React, { useState, useEffect } from 'react'
import { FaFolder, FaFileLines, FaHouse } from 'react-icons/fa6'
import { theme } from '../../styles/theme'
import { api } from '../../services/api'

interface TreeNodeProps {
  folder: any
  level?: number
  onNameClick: (nodeData: any) => void
  expandedFolders: Set<string>
  folderChildren: Map<string, any[]>
  loadRelatedDocuments: (folderId: string, folderName: string) => void
  selectedFolderId?: string | null
}

const TreeNode: React.FC<TreeNodeProps> = ({ 
  folder, 
  level = 0, 
  onNameClick, 
  expandedFolders, 
  folderChildren, 
  loadRelatedDocuments,
  selectedFolderId
}) => {
  const isExpanded = expandedFolders.has(folder._id || folder.id)
  const children = folderChildren.get(folder._id || folder.id) || []
  const isSelected = selectedFolderId === (folder._id || folder.id)
  
  const handleClick = () => {
    // Sempre chamar o callback de clique (navegação)
    onNameClick(folder)
  }
  
  return (
    <div style={{ marginLeft: `${level * 20}px` }}>
      <div
        style={{
          padding: '8px 12px',
          margin: '2px 0',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: isSelected ? theme.colors.primary : 'transparent',
          color: isSelected ? 'white' : theme.colors.primary,
          fontWeight: isSelected ? '600' : '500',
          border: isSelected ? `2px solid ${theme.colors.primary}` : '1px solid transparent',
          boxShadow: isSelected ? `0 4px 12px ${theme.colors.primary}30` : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px'
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = `${theme.colors.primary}10`
            e.currentTarget.style.borderColor = `${theme.colors.primary}30`
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.borderColor = 'transparent'
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {children.length > 0 && (
            <span style={{
              color: isSelected ? 'white' : theme.colors.primary,
              fontSize: '10px',
              fontWeight: 'bold',
              width: '12px',
              textAlign: 'center'
            }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          {folder.name === 'Início' ? (
            <FaHouse 
              style={{ 
                color: isSelected ? 'white' : theme.colors.primary,
                fontSize: '14px',
                opacity: 0.8
              }} 
            />
          ) : (
            <FaFolder 
              style={{ 
                color: isSelected ? 'white' : theme.colors.primary,
                fontSize: '14px',
                opacity: 0.8
              }} 
            />
          )}
        </div>
        <span style={{
          color: isSelected ? 'white' : theme.colors.primary,
          fontWeight: isSelected ? '600' : '500',
          fontSize: '13px',
          flex: 1
        }}>
          {folder.name}
        </span>
        {children.length > 0 && (
          <span style={{
            color: isSelected ? 'white' : theme.colors.primary,
            fontSize: '11px',
            opacity: 0.7
          }}>
            ({children.length})
          </span>
        )}
      </div>
      
      {/* Renderizar filhos se a pasta estiver expandida */}
      {isExpanded && children.length > 0 && (
        <div style={{ marginLeft: '16px' }}>
          {children.map((child: any, index: number) => (
            <TreeNode 
              key={child._id || child.id || index} 
              folder={child} 
              level={level + 1}
              onNameClick={onNameClick}
              expandedFolders={expandedFolders}
              folderChildren={folderChildren}
              loadRelatedDocuments={loadRelatedDocuments}
              selectedFolderId={selectedFolderId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CustomFolderTreeProps {
  treeData: any
  onNameClick: (nodeData: any) => void
  selectedFolderId?: string | null
  style?: React.CSSProperties
}

const CustomFolderTree: React.FC<CustomFolderTreeProps> = ({ 
  treeData, 
  onNameClick, 
  selectedFolderId,
  style 
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [folderChildren, setFolderChildren] = useState<Map<string, any[]>>(new Map())

  // Função para buscar documentos relacionados a uma pasta
  const loadRelatedDocuments = async (folderId: string, folderName: string) => {
    try {
      // Verificar se já temos os filhos em cache
      if (folderChildren.has(folderId)) {
        // Apenas expandir se já temos os dados
        setExpandedFolders(prev => new Set(prev).add(folderId))
        return
      }
      
      // Fazer uma nova busca na API especificamente para esta pasta
      const response = await api.get('/documents', {
        params: {
          page: 1,
          perPage: 5000,
          directory: JSON.stringify(['/', folderId]),
        },
      });
      
      if (response.data?.data) {
        // Filtrar apenas pastas (isFolder = true) e ordenar alfabeticamente
        const relatedFolders = response.data.data
          .filter((doc: any) => doc.isFolder === true)
          .sort((a: any, b: any) => {
            return a.name.localeCompare(b.name, 'pt-BR', { 
              sensitivity: 'base',
              numeric: true 
            });
          })
        
        // Salvar no cache e expandir (apenas pastas)
        setFolderChildren(prev => new Map(prev).set(folderId, relatedFolders))
        setExpandedFolders(prev => new Set(prev).add(folderId))
      }
      
    } catch (error) {
      console.error('Erro ao buscar documentos relacionados:', error)
    }
  }

  // Função para lidar com clique na árvore
  const handleNameClick = (nodeData: any) => {
    // Apenas pastas são exibidas na árvore, então sempre é uma pasta
    if (nodeData.isFolder) {
      // Primeiro expandir/colapsar, depois navegar
      const isExpanded = expandedFolders.has(nodeData._id || nodeData.id)
      if (isExpanded) {
        // Recolher pasta
        setExpandedFolders(prev => {
          const newSet = new Set(prev);
          newSet.delete(nodeData._id || nodeData.id);
          return newSet;
        });
      } else {
        // Expandir pasta
        loadRelatedDocuments(nodeData._id || nodeData.id, nodeData.name);
      }
      
      // Navegar para a pasta (carregar documentos no centro)
      onNameClick(nodeData)
    }
  }

  if (!treeData) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: theme.colors.primary,
        fontSize: '14px'
      }}>
        Carregando estrutura...
      </div>
    )
  }

  return (
    <div style={{ 
      padding: '8px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      ...style
    }}>
      <div style={{ 
        flex: 1,
        overflow: 'auto',
        padding: '4px'
      }}>
        {treeData.children && treeData.children.length > 0 ? (
          treeData.children.map((folder: any, index: number) => (
            <TreeNode 
              key={folder._id || folder.id || index} 
              folder={folder} 
              level={0}
              onNameClick={handleNameClick}
              expandedFolders={expandedFolders}
              folderChildren={folderChildren}
              loadRelatedDocuments={loadRelatedDocuments}
              selectedFolderId={selectedFolderId}
            />
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#666',
            fontSize: '14px'
          }}>
            Nenhum item encontrado
          </div>
        )}
      </div>
      
      <style>{`
        /* Scroll personalizado */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme.colors.primary}40;
          border-radius: 3px;
          transition: background 0.3s ease;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${theme.colors.primary}60;
        }
      `}</style>
    </div>
  )
}

export default CustomFolderTree
